import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { message, Spin, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCustomization } from '../../../contexts/CustomizationContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSave, faUndo, faPen, faTrash, faCheck,
    faEnvelope, faShareNodes, faGlobe, faCodeBranch, faUser, faCamera, faBriefcase, faHashtag, faPlay, faNewspaper,
    faChevronUp, faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import { RightOutlined, DownOutlined } from '@ant-design/icons';
import RgbColorSelector from './RgbColorSelector';
import ThemeLivePreview from './ThemeLivePreview';
import '../../../styles/settings/settings-common.css';
import '../../../styles/settings/customization.css';

const CUSTOM_SECTION_IDS = ['branding', 'contact', 'theme'];
const CUSTOMIZATION_AUTOSAVE_DEBOUNCE_MS = 2000;
const THEME_PRESET_CARD_WIDTH_PX = 140;
const THEME_PRESET_GRID_GAP_PX = 12;

const countThemePresetsPerRow = (containerWidthPx) => {
    if (containerWidthPx <= 0) return 1;
    const { floor, max } = Math;
    return max(1, floor((containerWidthPx + THEME_PRESET_GRID_GAP_PX) / (THEME_PRESET_CARD_WIDTH_PX + THEME_PRESET_GRID_GAP_PX)));
};

const parseCustomHash = (hash) => {
    if (!hash) return 'branding';
    const sub = hash.startsWith('customization-') ? hash.slice(14) : hash;
    return CUSTOM_SECTION_IDS.includes(sub) ? sub : 'branding';
};

const Customization = ({ embedded, unifiedPage }) => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const contentRef = useRef(null);
    const { customization: savedCustomization, applyTheme } = useCustomization();
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [, setCustomization] = useState(null);
    
    const hashRaw = location.hash ? location.hash.slice(1) : '';
    const activeSection = parseCustomHash(hashRaw);
    
    // Get the initial theme section from localStorage or default to 'layout_border'
    const getInitialThemeSection = () => {
        const savedSection = localStorage.getItem('customizationActiveThemeSection');
        const validSections = ['layout_border', 'confirm_buttons', 'cancel_buttons', 'login_buttons', 'signup_buttons', 'guest_buttons', 'pills', 'sidebar_menu'];
        return savedSection && validSections.includes(savedSection) ? savedSection : 'layout_border';
    };
    
    const [formData, setFormData] = useState({});
    const skipSaveAfterLanguageChangeRef = useRef(false);

    useEffect(() => {
        if (!embedded || unifiedPage) return;
        if (initialLoading) return;
        const scrollArea = contentRef.current;
        const container = embedded ? scrollArea : scrollArea?.parentElement;
        const sub = parseCustomHash(hashRaw);
        if (CUSTOM_SECTION_IDS.includes(sub)) {
            const el = document.getElementById(`customization-section-${sub}`);
            if (el && container) {
                const targetTop = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 12;
                container.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' });
            } else if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [embedded, unifiedPage, location.hash, initialLoading]);

    useEffect(() => {
        if (!embedded || unifiedPage) return;
        const scrollArea = contentRef.current;
        const container = embedded ? scrollArea : scrollArea?.parentElement;
        if (!container) return;
        const observer = new IntersectionObserver(
            () => {},
            { root: container, rootMargin: '-72px 0px -48% 0px', threshold: 0.05 }
        );
        CUSTOM_SECTION_IDS.forEach(id => {
            const el = document.getElementById(`customization-section-${id}`);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, [embedded, unifiedPage]);
    const [expandedProviders, setExpandedProviders] = useState({});
    
    // Theme-specific state
    const [themes, setThemes] = useState([]);
    const [selectedTheme, setSelectedTheme] = useState(null); // Theme currently selected for editing
    const [, setSelectedThemeId] = useState(null); // ID of theme selected for editing
    const [currentThemeId, setCurrentThemeId] = useState('default'); // Track which theme is currently active
    const [showSetCurrentConfirm, setShowSetCurrentConfirm] = useState(false);
    const [themeToSetCurrent, setThemeToSetCurrent] = useState(null);
    const [editingThemeId, setEditingThemeId] = useState(null);
    const [editingThemeName, setEditingThemeName] = useState('');
    const [isSelectingNewTheme, setIsSelectingNewTheme] = useState(false);
    const [currentColors, setCurrentColors] = useState({
        layout_border_color: '#000000',
        layout_border_text_color: '#FFFFFF',
        layout_border_menu_color: '#000000',
        layout_border_menu_hover_color: '#000000',
        layout_border_menu_text_color: '#FFFFFF',
        layout_border_menu_hover_text_color: '#FFFFFF',
        confirm_buttons_color: '#000000',
        confirm_buttons_hover_color: '#000000',
        confirm_buttons_text_color: '#FFFFFF',
        confirm_buttons_hover_text_color: '#FFFFFF',
        cancel_buttons_color: '#000000',
        cancel_buttons_hover_color: '#000000',
        cancel_buttons_text_color: '#FFFFFF',
        cancel_buttons_hover_text_color: '#FFFFFF',
        pills_color: '#000000',
        pills_text_color: '#FFFFFF',
        sidebar_menu_bg_color: '#1a1a2e',
        sidebar_menu_item_color: '#b3b3b3',
        sidebar_menu_item_hover_bg_color: '#5E5BFF',
        sidebar_menu_item_active_color: '#5E5BFF',
        sidebar_menu_opacity: '0.5',
        login_buttons_color: '#5E5BFF',
        login_buttons_text_color: '#FFFFFF',
        login_buttons_hover_color: '#4A47CC',
        login_buttons_hover_text_color: '#FFFFFF',
        signup_buttons_color: '#5E5BFF',
        signup_buttons_text_color: '#FFFFFF',
        signup_buttons_hover_color: '#4A47CC',
        signup_buttons_hover_text_color: '#FFFFFF',
        guest_buttons_color: '#FFFFFF',
        guest_buttons_text_color: '#5E5BFF',
        guest_buttons_hover_color: '#FFFFFF',
        guest_buttons_hover_text_color: '#4A47CC'
    });
    const [, setSelectedColorKey] = useState('layout_border_color');
    const [selectedThemeSection, setSelectedThemeSection] = useState(getInitialThemeSection);
    const [selectedColorOption, setSelectedColorOption] = useState(null);
    const getInitialThemeTab = () => {
        const saved = localStorage.getItem('customizationThemeTab');
        return saved && ['presets', 'layout', 'buttons'].includes(saved) ? saved : 'presets';
    };
    const [themeTab, setThemeTab] = useState(getInitialThemeTab);
    const [presetsPage, setPresetsPage] = useState(1);
    const [presetsPerPage, setPresetsPerPage] = useState(6);
    const presetsRowMeasureRef = useRef(null);
    const [defaultTheme, setDefaultTheme] = useState(null);
    const [themesLoaded, setThemesLoaded] = useState(false);

    const gridThemesCount = useMemo(() => {
        const allThemes = defaultTheme ? [defaultTheme, ...themes] : themes;
        return allThemes.filter((th) => th.id !== currentThemeId).length;
    }, [themes, defaultTheme, currentThemeId]);

    useLayoutEffect(() => {
        if (themeTab !== 'presets' || gridThemesCount === 0) return undefined;
        const el = presetsRowMeasureRef.current;
        if (!el) return undefined;
        const measure = () => {
            setPresetsPerPage(countThemePresetsPerRow(el.clientWidth));
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [themeTab, gridThemesCount]);

    useEffect(() => {
        const totalPages = Math.max(1, Math.ceil(gridThemesCount / presetsPerPage));
        setPresetsPage((p) => Math.min(Math.max(1, p), totalPages));
    }, [gridThemesCount, presetsPerPage]);
    const availableTabs = [
        { id: 'branding', label: t('admin.customization.branding'), description: t('admin.customization.brandingSettings') },
        { id: 'contact', label: t('admin.customization.contact'), description: t('admin.customization.contactInformation') },
        { id: 'theme', label: t('admin.customization.theme'), description: t('admin.customization.themeSettings') },
    ];

    const THEME_TABS = [
        { id: 'presets', label: t('admin.customization.presets') },
        { id: 'layout', label: t('admin.customization.layout') },
        { id: 'buttons', label: t('admin.customization.buttons') }
    ];

    const layoutOptions = [
        { key: 'layout_border_color', label: t('admin.customization.colorBorder') },
        { key: 'layout_border_menu_color', label: t('admin.customization.colorMenu') },
        { key: 'layout_border_menu_hover_color', label: t('admin.customization.colorMenuHover') },
        { key: 'layout_border_text_color', label: t('admin.customization.colorBorderText') },
        { key: 'layout_border_menu_text_color', label: t('admin.customization.colorMenuText') },
        { key: 'layout_border_menu_hover_text_color', label: t('admin.customization.colorMenuHoverText') }
    ];

    const pillsOptions = [
        { key: 'pills_color', label: t('admin.customization.colorPills') },
        { key: 'pills_text_color', label: t('admin.customization.colorPillsText') }
    ];

    const confirmButtonOptions = [
        { key: 'confirm_buttons_color', label: t('admin.customization.colorButton') },
        { key: 'confirm_buttons_hover_color', label: t('admin.customization.colorButtonHover') },
        { key: 'confirm_buttons_text_color', label: t('admin.customization.colorButtonText') },
        { key: 'confirm_buttons_hover_text_color', label: t('admin.customization.colorButtonHoverText') }
    ];

    const cancelButtonOptions = [
        { key: 'cancel_buttons_color', label: t('admin.customization.colorButton') },
        { key: 'cancel_buttons_hover_color', label: t('admin.customization.colorButtonHover') },
        { key: 'cancel_buttons_text_color', label: t('admin.customization.colorButtonText') },
        { key: 'cancel_buttons_hover_text_color', label: t('admin.customization.colorButtonHoverText') }
    ];

    const loginButtonOptions = [
        { key: 'login_buttons_color', label: t('admin.customization.colorButton') },
        { key: 'login_buttons_hover_color', label: t('admin.customization.colorButtonHover') },
        { key: 'login_buttons_text_color', label: t('admin.customization.colorButtonText') },
        { key: 'login_buttons_hover_text_color', label: t('admin.customization.colorButtonHoverText') }
    ];

    const signupButtonOptions = [
        { key: 'signup_buttons_color', label: t('admin.customization.colorButton') },
        { key: 'signup_buttons_hover_color', label: t('admin.customization.colorButtonHover') },
        { key: 'signup_buttons_text_color', label: t('admin.customization.colorButtonText') },
        { key: 'signup_buttons_hover_text_color', label: t('admin.customization.colorButtonHoverText') }
    ];

    const guestButtonOptions = [
        { key: 'guest_buttons_color', label: t('admin.customization.colorButton') },
        { key: 'guest_buttons_hover_color', label: t('admin.customization.colorButtonHover') },
        { key: 'guest_buttons_text_color', label: t('admin.customization.colorButtonText') },
        { key: 'guest_buttons_hover_text_color', label: t('admin.customization.colorButtonHoverText') }
    ];

    const sidebarOptions = [
        { key: 'sidebar_menu_bg_color', label: t('admin.customization.colorSidebarBackground') },
        { key: 'sidebar_menu_item_color', label: t('admin.customization.colorSidebarItem') },
        { key: 'sidebar_menu_item_hover_bg_color', label: t('admin.customization.colorSidebarItemHover') },
        { key: 'sidebar_menu_item_active_color', label: t('admin.customization.colorSidebarItemActive') },
        { key: 'sidebar_menu_opacity', label: t('admin.customization.opacity'), type: 'opacity' }
    ];

    const themeSections = [
        {
            id: 'layout_border',
            label: t('admin.customization.themeSectionLayoutBorder'),
            colors: [
                { key: 'layout_border_color', label: t('admin.customization.colorBorder') },
                { key: 'layout_border_menu_color', label: t('admin.customization.colorMenu') },
                { key: 'layout_border_menu_hover_color', label: t('admin.customization.colorMenuHover') },
                { key: 'layout_border_text_color', label: t('admin.customization.colorBorderText') },
                { key: 'layout_border_menu_text_color', label: t('admin.customization.colorMenuText') },
                { key: 'layout_border_menu_hover_text_color', label: t('admin.customization.colorMenuHoverText') }
            ]
        },
        {
            id: 'confirm_buttons',
            label: t('admin.customization.themeSectionConfirmButtons'),
            colors: [
                { key: 'confirm_buttons_color', label: t('admin.customization.colorButton') },
                { key: 'confirm_buttons_hover_color', label: t('admin.customization.colorButtonHover') },
                { key: 'confirm_buttons_text_color', label: t('admin.customization.colorButtonText') },
                { key: 'confirm_buttons_hover_text_color', label: t('admin.customization.colorButtonHoverText') }
            ]
        },
        {
            id: 'cancel_buttons',
            label: t('admin.customization.themeSectionCancelButtons'),
            colors: [
                { key: 'cancel_buttons_color', label: t('admin.customization.colorButton') },
                { key: 'cancel_buttons_hover_color', label: t('admin.customization.colorButtonHover') },
                { key: 'cancel_buttons_text_color', label: t('admin.customization.colorButtonText') },
                { key: 'cancel_buttons_hover_text_color', label: t('admin.customization.colorButtonHoverText') }
            ]
        },
        {
            id: 'login_buttons',
            label: t('admin.customization.loginButton'),
            colors: loginButtonOptions
        },
        {
            id: 'signup_buttons',
            label: t('admin.customization.signupButton'),
            colors: signupButtonOptions
        },
        {
            id: 'guest_buttons',
            label: t('admin.customization.guestButton'),
            colors: guestButtonOptions
        },
        {
            id: 'pills',
            label: t('admin.customization.themeSectionPills'),
            colors: [
                { key: 'pills_color', label: t('admin.customization.colorPills') },
                { key: 'pills_text_color', label: t('admin.customization.colorPillsText') }
            ]
        },
        {
            id: 'sidebar_menu',
            label: t('admin.customization.sidebarMenu'),
            colors: [
                { key: 'sidebar_menu_bg_color', label: t('admin.customization.colorSidebarBackground') },
                { key: 'sidebar_menu_item_color', label: t('admin.customization.colorSidebarItem') },
                { key: 'sidebar_menu_item_hover_bg_color', label: t('admin.customization.colorSidebarItemHover') },
                { key: 'sidebar_menu_item_active_color', label: t('admin.customization.colorSidebarItemActive') },
                { key: 'sidebar_menu_opacity', label: t('admin.customization.opacity'), type: 'opacity' }
            ]
        }
    ];

    const rgbChannelLabels = { r: t('admin.customization.colorChannelR'), g: t('admin.customization.colorChannelG'), b: t('admin.customization.colorChannelB') };

    useEffect(() => {
        fetchCustomizationSettings();
        fetchThemes();

        // Check for success message after page reload
        const successMessage = localStorage.getItem('themeUpdateSuccess');
        if (successMessage) {
            localStorage.removeItem('themeUpdateSuccess');
            message.success(successMessage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Load all customization data on mount

    useEffect(() => {
        const handler = () => {
            skipSaveAfterLanguageChangeRef.current = true;
        };
        i18n.on('languageChanged', handler);
        return () => { i18n.off('languageChanged', handler); };
    }, [i18n]);

    // Initialize selected color option when theme section or tab changes
    useEffect(() => {
        if (selectedThemeSection && !selectedColorOption) {
            const section = themeSections.find(s => s.id === selectedThemeSection);
            if (section && section.colors.length > 0) {
                setSelectedColorOption(section.colors[0].key);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedThemeSection, selectedColorOption]);

    useEffect(() => {
        if (themeTab === 'layout') setSelectedColorOption('layout_border_color');
        else if (themeTab === 'buttons') setSelectedColorOption('confirm_buttons_color');
    }, [themeTab]);

    /* Restore saved theme when leaving theme tab without applying */
    useEffect(() => {
        if (activeSection !== 'theme' && savedCustomization && applyTheme) {
            applyTheme(savedCustomization);
        }
    }, [activeSection, savedCustomization, applyTheme]);

    // Utility functions for color conversion
    const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    };

    const rgbToHex = (r, g, b) => {
        return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    };


    const fetchCustomizationSettings = async () => {
        try {
            setInitialLoading(true);
            const response = await fetch('/api/customization/', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const data = await response.json();
            
            if (data.success) {
                setCustomization(data.customization);
                
                // Set form data with current values
                setFormData({
                    // Branding
                    company_name: data.customization.branding_settings.company_name,
                    company_website: data.customization.branding_settings.company_website,
                    logo_url: data.customization.branding_settings.logo_url,
                    // Contact
                    contact_email: data.customization.contact_settings.contact_email,
                    contact_github: data.customization.contact_settings.contact_github,
                    contact_facebook: data.customization.contact_settings.contact_facebook,
                    contact_instagram: data.customization.contact_settings.contact_instagram,
                    contact_youtube: data.customization.contact_settings.contact_youtube,
                    contact_blog: data.customization.contact_settings.contact_blog,
                    contact_linkedin: data.customization.contact_settings.contact_linkedin,
                    contact_twitter: data.customization.contact_settings.contact_twitter,
                    // Theme Settings
                    layout_border_color: data.customization.theme_settings.layout_border_color,
                    layout_border_text_color: data.customization.theme_settings.layout_border_text_color,
                    layout_border_menu_color: data.customization.theme_settings.layout_border_menu_color,
                    layout_border_menu_hover_color: data.customization.theme_settings.layout_border_menu_hover_color,
                    layout_border_menu_text_color: data.customization.theme_settings.layout_border_menu_text_color,
                    layout_border_menu_hover_text_color: data.customization.theme_settings.layout_border_menu_hover_text_color,
                    confirm_buttons_color: data.customization.theme_settings.confirm_buttons_color,
                    confirm_buttons_hover_color: data.customization.theme_settings.confirm_buttons_hover_color,
                    confirm_buttons_text_color: data.customization.theme_settings.confirm_buttons_text_color,
                    confirm_buttons_hover_text_color: data.customization.theme_settings.confirm_buttons_hover_text_color,
                    cancel_buttons_color: data.customization.theme_settings.cancel_buttons_color,
                    cancel_buttons_hover_color: data.customization.theme_settings.cancel_buttons_hover_color,
                    cancel_buttons_text_color: data.customization.theme_settings.cancel_buttons_text_color,
                    cancel_buttons_hover_text_color: data.customization.theme_settings.cancel_buttons_hover_text_color,
                    pills_color: data.customization.theme_settings.pills_color,
                    pills_text_color: data.customization.theme_settings.pills_text_color,
                    sidebar_menu_bg_color: data.customization.theme_settings.sidebar_menu_bg_color || '#1a1a2e',
                    sidebar_menu_item_color: data.customization.theme_settings.sidebar_menu_item_color || '#b3b3b3',
                    sidebar_menu_item_hover_bg_color: data.customization.theme_settings.sidebar_menu_item_hover_bg_color || '#5E5BFF',
                    sidebar_menu_item_active_color: data.customization.theme_settings.sidebar_menu_item_active_color || '#5E5BFF',
                    sidebar_menu_opacity: data.customization.theme_settings.sidebar_menu_opacity || '0.5',
                    login_buttons_color: data.customization.theme_settings.login_buttons_color || '#5E5BFF',
                    login_buttons_text_color: data.customization.theme_settings.login_buttons_text_color || '#FFFFFF',
                    login_buttons_hover_color: data.customization.theme_settings.login_buttons_hover_color || '#4A47CC',
                    login_buttons_hover_text_color: data.customization.theme_settings.login_buttons_hover_text_color || '#FFFFFF',
                    signup_buttons_color: data.customization.theme_settings.signup_buttons_color || '#5E5BFF',
                    signup_buttons_text_color: data.customization.theme_settings.signup_buttons_text_color || '#FFFFFF',
                    signup_buttons_hover_color: data.customization.theme_settings.signup_buttons_hover_color || '#4A47CC',
                    signup_buttons_hover_text_color: data.customization.theme_settings.signup_buttons_hover_text_color || '#FFFFFF',
                    guest_buttons_color: data.customization.theme_settings.guest_buttons_color || '#FFFFFF',
                    guest_buttons_text_color: data.customization.theme_settings.guest_buttons_text_color || '#5E5BFF',
                    guest_buttons_hover_color: data.customization.theme_settings.guest_buttons_hover_color || '#FFFFFF',
                    guest_buttons_hover_text_color: data.customization.theme_settings.guest_buttons_hover_text_color || '#4A47CC',
                });
                
                // Set current colors for theme tab
                setCurrentColors({
                    layout_border_color: data.customization.theme_settings.layout_border_color,
                    layout_border_text_color: data.customization.theme_settings.layout_border_text_color,
                    layout_border_menu_color: data.customization.theme_settings.layout_border_menu_color,
                    layout_border_menu_hover_color: data.customization.theme_settings.layout_border_menu_hover_color,
                    layout_border_menu_text_color: data.customization.theme_settings.layout_border_menu_text_color,
                    layout_border_menu_hover_text_color: data.customization.theme_settings.layout_border_menu_hover_text_color,
                    confirm_buttons_color: data.customization.theme_settings.confirm_buttons_color,
                    confirm_buttons_hover_color: data.customization.theme_settings.confirm_buttons_hover_color,
                    confirm_buttons_text_color: data.customization.theme_settings.confirm_buttons_text_color,
                    confirm_buttons_hover_text_color: data.customization.theme_settings.confirm_buttons_hover_text_color,
                    cancel_buttons_color: data.customization.theme_settings.cancel_buttons_color,
                    cancel_buttons_hover_color: data.customization.theme_settings.cancel_buttons_hover_color,
                    cancel_buttons_text_color: data.customization.theme_settings.cancel_buttons_text_color,
                    cancel_buttons_hover_text_color: data.customization.theme_settings.cancel_buttons_hover_text_color,
                    pills_color: data.customization.theme_settings.pills_color,
                    pills_text_color: data.customization.theme_settings.pills_text_color,
                    sidebar_menu_bg_color: data.customization.theme_settings.sidebar_menu_bg_color || '#1a1a2e',
                    sidebar_menu_item_color: data.customization.theme_settings.sidebar_menu_item_color || '#b3b3b3',
                    sidebar_menu_item_hover_bg_color: data.customization.theme_settings.sidebar_menu_item_hover_bg_color || '#5E5BFF',
                    sidebar_menu_item_active_color: data.customization.theme_settings.sidebar_menu_item_active_color || '#5E5BFF',
                    sidebar_menu_opacity: data.customization.theme_settings.sidebar_menu_opacity || '0.5',
                    login_buttons_color: data.customization.theme_settings.login_buttons_color || '#5E5BFF',
                    login_buttons_text_color: data.customization.theme_settings.login_buttons_text_color || '#FFFFFF',
                    login_buttons_hover_color: data.customization.theme_settings.login_buttons_hover_color || '#4A47CC',
                    login_buttons_hover_text_color: data.customization.theme_settings.login_buttons_hover_text_color || '#FFFFFF',
                    signup_buttons_color: data.customization.theme_settings.signup_buttons_color || '#5E5BFF',
                    signup_buttons_text_color: data.customization.theme_settings.signup_buttons_text_color || '#FFFFFF',
                    signup_buttons_hover_color: data.customization.theme_settings.signup_buttons_hover_color || '#4A47CC',
                    signup_buttons_hover_text_color: data.customization.theme_settings.signup_buttons_hover_text_color || '#FFFFFF',
                    guest_buttons_color: data.customization.theme_settings.guest_buttons_color || '#FFFFFF',
                    guest_buttons_text_color: data.customization.theme_settings.guest_buttons_text_color || '#5E5BFF',
                    guest_buttons_hover_color: data.customization.theme_settings.guest_buttons_hover_color || '#FFFFFF',
                    guest_buttons_hover_text_color: data.customization.theme_settings.guest_buttons_hover_text_color || '#4A47CC',
                });
            } else {
                message.error(data.error || t('admin.customization.loadError'));
            }
        } catch (error) {
            console.error('Error fetching customization settings:', error);
            message.error(t('admin.customization.loadError'));
        } finally {
            setInitialLoading(false);
        }
    };

    const fetchThemes = async () => {
        try {
            // Fetch both themes and defaults
            const [themesResponse, defaultsResponse] = await Promise.all([
                fetch('/api/customization/themes/', {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                }),
                fetch('/api/customization/defaults/', {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                })
            ]);

            const themesData = await themesResponse.json();
            const defaultsData = await defaultsResponse.json();
            
            if (themesData.success) {
                // Sort themes by ID for consistent ordering
                const sortedThemes = themesData.customization_themes.sort((a, b) => {
                    // Convert IDs to numbers for proper numeric sorting, fallback to string comparison
                    const aId = isNaN(parseInt(a.id)) ? a.id : parseInt(a.id);
                    const bId = isNaN(parseInt(b.id)) ? b.id : parseInt(b.id);
                    
                    if (typeof aId === 'number' && typeof bId === 'number') {
                        return aId - bId;
                    }
                    return String(aId).localeCompare(String(bId));
                });
                setThemes(sortedThemes);
            } else {
                message.error(themesData.error || t('admin.customization.themeLoadFailed'));
            }

            if (defaultsData.success) {
                // Get the actual current theme ID and name from theme_settings
                const currentThemeActualId = defaultsData.defaults.theme_settings.id || 'default';
                const currentThemeActualName = defaultsData.defaults.theme_settings.name || t('admin.customization.currentThemeLabel');
                
                // Create a default theme object with the actual ID and name
                const defaultThemeObj = {
                    id: currentThemeActualId,
                    name: currentThemeActualName,
                    layout_border_color: defaultsData.defaults.theme_settings.layout_border_color,
                    layout_border_text_color: defaultsData.defaults.theme_settings.layout_border_text_color,
                    layout_border_menu_color: defaultsData.defaults.theme_settings.layout_border_menu_color,
                    layout_border_menu_hover_color: defaultsData.defaults.theme_settings.layout_border_menu_hover_color,
                    layout_border_menu_text_color: defaultsData.defaults.theme_settings.layout_border_menu_text_color,
                    layout_border_menu_hover_text_color: defaultsData.defaults.theme_settings.layout_border_menu_hover_text_color,
                    confirm_buttons_color: defaultsData.defaults.theme_settings.confirm_buttons_color,
                    confirm_buttons_hover_color: defaultsData.defaults.theme_settings.confirm_buttons_hover_color,
                    confirm_buttons_text_color: defaultsData.defaults.theme_settings.confirm_buttons_text_color,
                    confirm_buttons_hover_text_color: defaultsData.defaults.theme_settings.confirm_buttons_hover_text_color,
                    cancel_buttons_color: defaultsData.defaults.theme_settings.cancel_buttons_color,
                    cancel_buttons_hover_color: defaultsData.defaults.theme_settings.cancel_buttons_hover_color,
                    cancel_buttons_text_color: defaultsData.defaults.theme_settings.cancel_buttons_text_color,
                    cancel_buttons_hover_text_color: defaultsData.defaults.theme_settings.cancel_buttons_hover_text_color,
                    pills_color: defaultsData.defaults.theme_settings.pills_color,
                    pills_text_color: defaultsData.defaults.theme_settings.pills_text_color,
                    sidebar_menu_bg_color: defaultsData.defaults.theme_settings.sidebar_menu_bg_color || '#1a1a2e',
                    sidebar_menu_item_color: defaultsData.defaults.theme_settings.sidebar_menu_item_color || '#b3b3b3',
                    sidebar_menu_item_hover_bg_color: defaultsData.defaults.theme_settings.sidebar_menu_item_hover_bg_color || '#5E5BFF',
                    sidebar_menu_item_active_color: defaultsData.defaults.theme_settings?.sidebar_menu_item_active_color || '#5E5BFF',
                    sidebar_menu_opacity: defaultsData.defaults.theme_settings?.sidebar_menu_opacity || '0.5',
                    login_buttons_color: defaultsData.defaults.theme_settings?.login_buttons_color || '#5E5BFF',
                    login_buttons_text_color: defaultsData.defaults.theme_settings?.login_buttons_text_color || '#FFFFFF',
                    login_buttons_hover_color: defaultsData.defaults.theme_settings?.login_buttons_hover_color || '#4A47CC',
                    login_buttons_hover_text_color: defaultsData.defaults.theme_settings?.login_buttons_hover_text_color || '#FFFFFF',
                    signup_buttons_color: defaultsData.defaults.theme_settings?.signup_buttons_color || '#5E5BFF',
                    signup_buttons_text_color: defaultsData.defaults.theme_settings?.signup_buttons_text_color || '#FFFFFF',
                    signup_buttons_hover_color: defaultsData.defaults.theme_settings?.signup_buttons_hover_color || '#4A47CC',
                    signup_buttons_hover_text_color: defaultsData.defaults.theme_settings?.signup_buttons_hover_text_color || '#FFFFFF',
                    guest_buttons_color: defaultsData.defaults.theme_settings?.guest_buttons_color || '#FFFFFF',
                    guest_buttons_text_color: defaultsData.defaults.theme_settings?.guest_buttons_text_color || '#5E5BFF',
                    guest_buttons_hover_color: defaultsData.defaults.theme_settings?.guest_buttons_hover_color || '#FFFFFF',
                    guest_buttons_hover_text_color: defaultsData.defaults.theme_settings?.guest_buttons_hover_text_color || '#4A47CC',
                };
                setDefaultTheme(defaultThemeObj);
                
                // The customization_theme_id tells us which theme is currently active
                const currentCustomizationThemeId = defaultsData.defaults.customization_theme_id;
                
                let currentThemeToSelect;
                let currentThemeRadioId;
                
                // Look for matching preset based on customization_theme_id
                const matchingPreset = themesData.customization_themes?.find(
                    theme => theme.id === currentCustomizationThemeId
                );
                
                if (matchingPreset) {
                    // Current theme is one of the presets
                    currentThemeRadioId = matchingPreset.id;
                    currentThemeToSelect = matchingPreset;
                } else {
                    // Current theme is the default theme (not a preset)
                    currentThemeRadioId = currentThemeActualId;
                    currentThemeToSelect = defaultThemeObj;
                }
                
                setCurrentThemeId(currentThemeRadioId);
                
                // Check for previously selected theme in localStorage
                const savedSelectedThemeId = localStorage.getItem('selectedThemeId');
                let themeToSelect = currentThemeToSelect; // Default to current theme
                
                if (savedSelectedThemeId) {
                    // Try to find the previously selected theme
                    if (savedSelectedThemeId === currentThemeActualId || savedSelectedThemeId === String(currentCustomizationThemeId)) {
                        // Previously selected theme is the current theme
                        themeToSelect = defaultThemeObj;
                    } else {
                        // Look for the previously selected theme in presets
                        const previouslySelectedTheme = themesData.customization_themes?.find(
                            theme => String(theme.id) === String(savedSelectedThemeId)
                        );
                        if (previouslySelectedTheme) {
                            themeToSelect = previouslySelectedTheme;
                        }
                        // If not found, will fall back to current theme (themeToSelect remains unchanged)
                    }
                }
                
                // Select the determined theme
                setSelectedTheme(themeToSelect);
                setSelectedThemeId(themeToSelect.id);
                
                // Update colors to match the selected theme
                setCurrentColors({
                    layout_border_color: themeToSelect.layout_border_color,
                    layout_border_text_color: themeToSelect.layout_border_text_color,
                    layout_border_menu_color: themeToSelect.layout_border_menu_color,
                    layout_border_menu_hover_color: themeToSelect.layout_border_menu_hover_color,
                    layout_border_menu_text_color: themeToSelect.layout_border_menu_text_color,
                    layout_border_menu_hover_text_color: themeToSelect.layout_border_menu_hover_text_color,
                    confirm_buttons_color: themeToSelect.confirm_buttons_color,
                    confirm_buttons_hover_color: themeToSelect.confirm_buttons_hover_color,
                    confirm_buttons_text_color: themeToSelect.confirm_buttons_text_color,
                    confirm_buttons_hover_text_color: themeToSelect.confirm_buttons_hover_text_color,
                    cancel_buttons_color: themeToSelect.cancel_buttons_color,
                    cancel_buttons_hover_color: themeToSelect.cancel_buttons_hover_color,
                    cancel_buttons_text_color: themeToSelect.cancel_buttons_text_color,
                    cancel_buttons_hover_text_color: themeToSelect.cancel_buttons_hover_text_color,
                                    pills_color: themeToSelect.pills_color,
                                    pills_text_color: themeToSelect.pills_text_color,
                                    sidebar_menu_bg_color: themeToSelect.sidebar_menu_bg_color || '#1a1a2e',
                                    sidebar_menu_item_color: themeToSelect.sidebar_menu_item_color || '#b3b3b3',
                                    sidebar_menu_item_hover_bg_color: themeToSelect.sidebar_menu_item_hover_bg_color || '#5E5BFF',
                                    sidebar_menu_item_active_color: themeToSelect.sidebar_menu_item_active_color || '#5E5BFF',
                                    sidebar_menu_opacity: themeToSelect.sidebar_menu_opacity || '0.5',
                                    login_buttons_color: themeToSelect.login_buttons_color || '#5E5BFF',
                                    login_buttons_text_color: themeToSelect.login_buttons_text_color || '#FFFFFF',
                                    login_buttons_hover_color: themeToSelect.login_buttons_hover_color || '#4A47CC',
                                    login_buttons_hover_text_color: themeToSelect.login_buttons_hover_text_color || '#FFFFFF',
                                    signup_buttons_color: themeToSelect.signup_buttons_color || '#5E5BFF',
                                    signup_buttons_text_color: themeToSelect.signup_buttons_text_color || '#FFFFFF',
                                    signup_buttons_hover_color: themeToSelect.signup_buttons_hover_color || '#4A47CC',
                                    signup_buttons_hover_text_color: themeToSelect.signup_buttons_hover_text_color || '#FFFFFF',
                                    guest_buttons_color: themeToSelect.guest_buttons_color || '#FFFFFF',
                                    guest_buttons_text_color: themeToSelect.guest_buttons_text_color || '#5E5BFF',
                                    guest_buttons_hover_color: themeToSelect.guest_buttons_hover_color || '#FFFFFF',
                                    guest_buttons_hover_text_color: themeToSelect.guest_buttons_hover_text_color || '#4A47CC',
                                });
            } else {
                message.error(defaultsData.error || t('admin.customization.themeDefaultLoadFailed'));
            }
            
            // Mark themes as loaded to prevent refetching
            setThemesLoaded(true);
        } catch (error) {
            console.error('Error fetching themes:', error);
            message.error(t('admin.customization.themeLoadFailed'));
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        const activeEl = document.activeElement;
        try {
            setLoading(true);
            
            const updateData = {
                branding_settings: {
                    company_name: formData.company_name,
                    logo_url: formData.logo_url,
                },
                contact_settings: {
                    contact_email: formData.contact_email,
                    company_website: formData.company_website,
                    contact_github: formData.contact_github,
                    contact_facebook: formData.contact_facebook,
                    contact_instagram: formData.contact_instagram,
                    contact_youtube: formData.contact_youtube,
                    contact_blog: formData.contact_blog,
                    contact_linkedin: formData.contact_linkedin,
                    contact_twitter: formData.contact_twitter,
                },
                theme_settings: {
                    layout_border_color: formData.layout_border_color,
                    layout_border_text_color: formData.layout_border_text_color,
                    layout_border_menu_color: formData.layout_border_menu_color,
                    layout_border_menu_hover_color: formData.layout_border_menu_hover_color,
                    layout_border_menu_text_color: formData.layout_border_menu_text_color,
                    layout_border_menu_hover_text_color: formData.layout_border_menu_hover_text_color,
                    confirm_buttons_color: formData.confirm_buttons_color,
                    confirm_buttons_hover_color: formData.confirm_buttons_hover_color,
                    confirm_buttons_text_color: formData.confirm_buttons_text_color,
                    confirm_buttons_hover_text_color: formData.confirm_buttons_hover_text_color,
                    cancel_buttons_color: formData.cancel_buttons_color,
                    cancel_buttons_hover_color: formData.cancel_buttons_hover_color,
                    cancel_buttons_text_color: formData.cancel_buttons_text_color,
                    cancel_buttons_hover_text_color: formData.cancel_buttons_hover_text_color,
                                    pills_color: formData.pills_color,
                                    pills_text_color: formData.pills_text_color,
                                    sidebar_menu_bg_color: formData.sidebar_menu_bg_color || '#1a1a2e',
                                    sidebar_menu_item_color: formData.sidebar_menu_item_color || '#b3b3b3',
                                    sidebar_menu_item_hover_bg_color: formData.sidebar_menu_item_hover_bg_color || '#5E5BFF',
                                    sidebar_menu_item_active_color: formData.sidebar_menu_item_active_color || '#5E5BFF',
                                    sidebar_menu_opacity: formData.sidebar_menu_opacity || '0.5',
                                    login_buttons_color: formData.login_buttons_color || '#5E5BFF',
                                    login_buttons_text_color: formData.login_buttons_text_color || '#FFFFFF',
                                    login_buttons_hover_color: formData.login_buttons_hover_color || '#4A47CC',
                                    login_buttons_hover_text_color: formData.login_buttons_hover_text_color || '#FFFFFF',
                                    signup_buttons_color: formData.signup_buttons_color || '#5E5BFF',
                                    signup_buttons_text_color: formData.signup_buttons_text_color || '#FFFFFF',
                                    signup_buttons_hover_color: formData.signup_buttons_hover_color || '#4A47CC',
                                    signup_buttons_hover_text_color: formData.signup_buttons_hover_text_color || '#FFFFFF',
                                    guest_buttons_color: formData.guest_buttons_color || '#FFFFFF',
                                    guest_buttons_text_color: formData.guest_buttons_text_color || '#5E5BFF',
                                    guest_buttons_hover_color: formData.guest_buttons_hover_color || '#FFFFFF',
                                    guest_buttons_hover_text_color: formData.guest_buttons_hover_text_color || '#4A47CC',
                                },
            };

            const response = await fetch('/api/customization/update/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(updateData),
            });

            const data = await response.json();
            
            if (data.success) {
                message.success(t('admin.customization.updateSuccess'));
                setCustomization(data.customization);
                // Trigger customization context refresh if available
                if (window.refreshCustomization) {
                    window.refreshCustomization();
                }
            } else {
                message.error(data.error || t('admin.customization.updateError'));
            }
        } catch (error) {
            console.error('Error updating customization:', error);
            message.error(t('admin.customization.updateError'));
        } finally {
            setLoading(false);
            if (activeEl && typeof activeEl.focus === 'function' && document.body.contains(activeEl)) {
                requestAnimationFrame(() => requestAnimationFrame(() => activeEl.focus({ preventScroll: true })));
            }
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const hasInitializedRef = useRef(false);
    const saveDebounceRef = useRef(null);
    useEffect(() => {
        if (initialLoading) return;
        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            return;
        }
        if (skipSaveAfterLanguageChangeRef.current) {
            skipSaveAfterLanguageChangeRef.current = false;
            return;
        }
        if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
        saveDebounceRef.current = setTimeout(() => handleSubmit(), CUSTOMIZATION_AUTOSAVE_DEBOUNCE_MS);
        return () => { if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current); };
    }, [
        initialLoading,
        formData.company_name, formData.company_website, formData.logo_url,
        formData.contact_email, formData.contact_github, formData.contact_facebook,
        formData.contact_instagram, formData.contact_youtube, formData.contact_blog,
        formData.contact_linkedin, formData.contact_twitter,
    ]);

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            message.error(t('admin.customization.logoTooLarge'));
            return;
        }
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (!['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
            message.error(t('admin.customization.logoInvalidType'));
            return;
        }
        try {
            setLoading(true);
            const formDataUpload = new FormData();
            formDataUpload.append('logo', file);
            const response = await fetch('/api/customization/logo/upload/', {
                method: 'POST',
                credentials: 'include',
                body: formDataUpload,
            });
            const data = await response.json();
            if (data.success) {
                setFormData(prev => ({ ...prev, logo_url: data.logo_url }));
                if (window.refreshCustomization) window.refreshCustomization();
                message.success(t('admin.customization.logoUploadSuccess'));
            } else {
                message.error(data.error || t('admin.customization.logoUploadError'));
            }
        } catch (error) {
            console.error('Logo upload error:', error);
            message.error(t('admin.customization.logoUploadError'));
        } finally {
            setLoading(false);
            e.target.value = '';
        }
    };

    // Handle provider expansion toggle
    const handleProviderToggle = (providerId) => {
        setExpandedProviders(prev => ({
            ...prev,
            [providerId]: !prev[providerId]
        }));
    };

    // Theme-specific functions
    const handleColorChange = (colorKey, newValue) => {
        setCurrentColors(prev => ({
            ...prev,
            [colorKey]: newValue
        }));
        setFormData(prev => ({ ...prev, [colorKey]: newValue }));
    };

    const handleOpacityChange = (value) => {
        const clamped = Math.max(0, Math.min(1, parseFloat(value) || 0.5));
        const str = String(clamped);
        setCurrentColors(prev => ({ ...prev, sidebar_menu_opacity: str }));
        setFormData(prev => ({ ...prev, sidebar_menu_opacity: str }));
    };

    const handleThemeSectionSelect = (sectionId) => {
        setSelectedThemeSection(sectionId);
        
        // Auto-select the first color option in the new section
        const section = themeSections.find(s => s.id === sectionId);
        if (section && section.colors.length > 0) {
            setSelectedColorOption(section.colors[0].key);
        } else {
            setSelectedColorOption(null);
        }
        
        // Save to localStorage
        localStorage.setItem('customizationActiveThemeSection', sectionId);
    };
    
    const handleColorOptionSelect = (colorKey) => {
        setSelectedColorOption(colorKey);
    };

    // Handle manual hex input changes
    const handleHexInputChange = (colorKey, hexValue) => {
        // Always update the state to allow typing
        let inputValue = hexValue.toUpperCase();
        
        // Ensure hex value starts with #
        if (!inputValue.startsWith('#') && inputValue.length > 0) {
            inputValue = '#' + inputValue;
        }
        
        // Always update the color state to allow real-time editing
        setCurrentColors(prev => ({
            ...prev,
            [colorKey]: inputValue
        }));
        
        // Validate and apply color if valid hex format for color picker sync
        const hexRegex = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/;
        if (hexRegex.test(inputValue)) {
            let cleanHex = inputValue;
            // Convert 3-digit hex to 6-digit
            if (cleanHex.length === 4) {
                cleanHex = '#' + cleanHex[1] + cleanHex[1] + cleanHex[2] + cleanHex[2] + cleanHex[3] + cleanHex[3];
            }
            // Update formData for saving
            setFormData(prev => ({
                ...prev,
                [colorKey]: cleanHex
            }));
        }
    };

    // Handle manual RGB input changes
    const handleRgbInputChange = (colorKey, component, value) => {
        // Handle empty input gracefully
        let numValue = value === '' ? 0 : parseInt(value) || 0;
        const clampedValue = Math.max(0, Math.min(255, numValue));
        
        const rgb = hexToRgb(currentColors[colorKey]);
        if (rgb) {
            const newRgb = { ...rgb, [component]: clampedValue };
            const newHex = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
            
            // Update current colors state
            setCurrentColors(prev => ({
                ...prev,
                [colorKey]: newHex
            }));
            
            // Update formData for saving
            setFormData(prev => ({
                ...prev,
                [colorKey]: newHex
            }));
        }
    };

    const handleRgbStep = (colorKey, component, delta) => {
        const rgb = hexToRgb(currentColors[colorKey]);
        if (rgb) {
            const current = rgb[component] ?? 0;
            const newValue = Math.max(0, Math.min(255, current + delta));
            handleRgbInputChange(colorKey, component, String(newValue));
        }
    };


    const handleThemeSelect = async (theme) => {
        // If we're currently editing a theme name, save it first
        if (editingThemeId && editingThemeName.trim()) {
            setIsSelectingNewTheme(true);
            
            // Find the theme being edited
            const editingTheme = editingThemeId === defaultTheme?.id ? defaultTheme : themes.find(t => t.id === editingThemeId);
            
            if (editingTheme && editingThemeName.trim() !== editingTheme.name) {
                // Save the current edit before switching
                await handleSaveEditThemeName(editingTheme);
            } else {
                // Just cancel editing if no change
                setEditingThemeId(null);
                setEditingThemeName('');
            }
            
            setIsSelectingNewTheme(false);
        }
        
        setSelectedTheme(theme);
        setSelectedThemeId(theme.id);
        
        // Save selected theme to localStorage
        localStorage.setItem('selectedThemeId', theme.id);
        
        setCurrentColors({
            layout_border_color: theme.layout_border_color,
            layout_border_text_color: theme.layout_border_text_color,
            layout_border_menu_color: theme.layout_border_menu_color,
            layout_border_menu_hover_color: theme.layout_border_menu_hover_color,
            layout_border_menu_text_color: theme.layout_border_menu_text_color,
            layout_border_menu_hover_text_color: theme.layout_border_menu_hover_text_color,
            confirm_buttons_color: theme.confirm_buttons_color,
            confirm_buttons_hover_color: theme.confirm_buttons_hover_color,
            confirm_buttons_text_color: theme.confirm_buttons_text_color,
            confirm_buttons_hover_text_color: theme.confirm_buttons_hover_text_color,
            cancel_buttons_color: theme.cancel_buttons_color,
            cancel_buttons_hover_color: theme.cancel_buttons_hover_color,
            cancel_buttons_text_color: theme.cancel_buttons_text_color,
            cancel_buttons_hover_text_color: theme.cancel_buttons_hover_text_color,
                    pills_color: theme.pills_color,
                    pills_text_color: theme.pills_text_color,
                    sidebar_menu_bg_color: theme.sidebar_menu_bg_color || '#1a1a2e',
                    sidebar_menu_item_color: theme.sidebar_menu_item_color || '#b3b3b3',
                    sidebar_menu_item_hover_bg_color: theme.sidebar_menu_item_hover_bg_color || '#5E5BFF',
                    sidebar_menu_item_active_color: theme.sidebar_menu_item_active_color || '#5E5BFF',
                    sidebar_menu_opacity: theme.sidebar_menu_opacity || '0.5',
                    login_buttons_color: theme.login_buttons_color || '#5E5BFF',
                    login_buttons_text_color: theme.login_buttons_text_color || '#FFFFFF',
                    login_buttons_hover_color: theme.login_buttons_hover_color || '#4A47CC',
                    login_buttons_hover_text_color: theme.login_buttons_hover_text_color || '#FFFFFF',
                    signup_buttons_color: theme.signup_buttons_color || '#5E5BFF',
                    signup_buttons_text_color: theme.signup_buttons_text_color || '#FFFFFF',
                    signup_buttons_hover_color: theme.signup_buttons_hover_color || '#4A47CC',
                    signup_buttons_hover_text_color: theme.signup_buttons_hover_text_color || '#FFFFFF',
                    guest_buttons_color: theme.guest_buttons_color || '#FFFFFF',
                    guest_buttons_text_color: theme.guest_buttons_text_color || '#5E5BFF',
                    guest_buttons_hover_color: theme.guest_buttons_hover_color || '#FFFFFF',
                    guest_buttons_hover_text_color: theme.guest_buttons_hover_text_color || '#4A47CC',
        });
        // Always default to layout border color when selecting a theme
        setSelectedColorKey('layout_border_color');
    };

    const handleRadioChange = (theme) => {
        if (theme.id === currentThemeId) {
            return; // Already current, no action needed
        }
        
        setThemeToSetCurrent(theme);
        setShowSetCurrentConfirm(true);
    };

    const confirmSetCurrentTheme = async () => {
        if (!themeToSetCurrent) return;
        
        try {
            setLoading(true);
            
            const response = await fetch('/api/customization/themes/set-current/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    theme_id: themeToSetCurrent.id
                }),
            });

            const data = await response.json();
            
            if (data.success) {
                localStorage.setItem('themeUpdateSuccess', data.message || t('admin.customization.themeSetCurrentSuccess'));
                const hash = window.location.hash ? window.location.hash.slice(1) : '';
                if (hash) localStorage.setItem('settingsHashAfterReload', hash);
                window.location.reload();
            } else {
                message.error(data.error || t('admin.customization.themeSetCurrentFailed'));
            }
        } catch (error) {
            console.error('Error setting current theme:', error);
            message.error(t('admin.customization.themeSetCurrentFailed'));
        } finally {
            setLoading(false);
            setShowSetCurrentConfirm(false);
            setThemeToSetCurrent(null);
        }
    };

    const cancelSetCurrentTheme = () => {
        setShowSetCurrentConfirm(false);
        setThemeToSetCurrent(null);
    };

    const handleEditThemeName = (theme) => {
        setEditingThemeId(theme.id);
        setEditingThemeName(theme.name);
    };

    const handleCancelEditThemeName = () => {
        setEditingThemeId(null);
        setEditingThemeName('');
    };

    const handleSaveEditThemeName = async (theme) => {
        if (!editingThemeName.trim()) {
            // If empty, just cancel
            handleCancelEditThemeName();
            return;
        }

        if (editingThemeName.trim() === theme.name) {
            // If no change, just cancel
            handleCancelEditThemeName();
            return;
        }

        try {
            setLoading(true);
            
            // Determine if this is the current theme
            const isCurrentTheme = theme.id === currentThemeId;
            
            const response = await fetch('/api/customization/themes/update/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    id: theme.id,
                    theme_name: editingThemeName.trim(),
                    is_current: isCurrentTheme
                }),
            });

            const data = await response.json();
            
            if (data.success || data.message) {
                // Only show success message if not selecting a new theme
                if (!isSelectingNewTheme) {
                    message.success(t('admin.customization.themeRenameSuccess'));
                }
                
                // Update local state
                if (isCurrentTheme) {
                    // Update default theme name
                    setDefaultTheme(prev => ({
                        ...prev,
                        name: editingThemeName.trim()
                    }));
                    
                    // If this theme is currently selected for editing, update it
                    if (selectedTheme?.id === theme.id) {
                        setSelectedTheme(prev => ({
                            ...prev,
                            name: editingThemeName.trim()
                        }));
                    }
                } else {
                    // Update themes list
                    setThemes(prev => prev.map(t => 
                        t.id === theme.id ? { ...t, name: editingThemeName.trim() } : t
                    ));
                    
                    // If this theme is currently selected for editing, update it
                    if (selectedTheme?.id === theme.id) {
                        setSelectedTheme(prev => ({
                            ...prev,
                            name: editingThemeName.trim()
                        }));
                    }
                }
                
                // Clear editing state
                setEditingThemeId(null);
                setEditingThemeName('');
            } else {
                message.error(data.error || t('admin.customization.themeRenameFailed'));
            }
        } catch (error) {
            console.error('Error updating theme name:', error);
            message.error(t('admin.customization.themeRenameFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTheme = (theme) => {
        Modal.confirm({
            title: t('admin.customization.themeDeleteTitle'),
            icon: null,
            content: t('admin.customization.themeDeleteConfirm', { name: theme.name }),
            okText: t('common.buttons.delete'),
            cancelText: t('common.buttons.cancel'),
            okType: 'danger',
            okButtonProps: {
                style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }
            },
            onOk: async () => {
                try {
                    setLoading(true);
                    
                    const response = await fetch('/api/customization/themes/delete/', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            theme_id: theme.id
                        }),
                    });

                    const data = await response.json();
                    
                    if (data.success) {
                        message.success(data.message || t('admin.customization.themeDeleteSuccess'));
                        
                        // If the deleted theme was selected for editing, clear selection
                        if (selectedTheme?.id === theme.id) {
                            // Select the default theme or first available theme
                            if (defaultTheme) {
                                setSelectedTheme(defaultTheme);
                                setSelectedThemeId(defaultTheme.id);
                                // Update localStorage to reflect the new selection
                                localStorage.setItem('selectedThemeId', defaultTheme.id);
                                setCurrentColors({
                                    layout_border_color: defaultTheme.layout_border_color,
                                    layout_border_text_color: defaultTheme.layout_border_text_color,
                                    layout_border_menu_color: defaultTheme.layout_border_menu_color,
                                    layout_border_menu_hover_color: defaultTheme.layout_border_menu_hover_color,
                                    layout_border_menu_text_color: defaultTheme.layout_border_menu_text_color,
                                    layout_border_menu_hover_text_color: defaultTheme.layout_border_menu_hover_text_color,
                                    confirm_buttons_color: defaultTheme.confirm_buttons_color,
                                    confirm_buttons_hover_color: defaultTheme.confirm_buttons_hover_color,
                                    confirm_buttons_text_color: defaultTheme.confirm_buttons_text_color,
                                    confirm_buttons_hover_text_color: defaultTheme.confirm_buttons_hover_text_color,
                                    cancel_buttons_color: defaultTheme.cancel_buttons_color,
                                    cancel_buttons_hover_color: defaultTheme.cancel_buttons_hover_color,
                                    cancel_buttons_text_color: defaultTheme.cancel_buttons_text_color,
                                    cancel_buttons_hover_text_color: defaultTheme.cancel_buttons_hover_text_color,
                                    pills_color: defaultTheme.pills_color,
                                    pills_text_color: defaultTheme.pills_text_color,
                                    sidebar_menu_bg_color: defaultTheme.sidebar_menu_bg_color || '#1a1a2e',
                                    sidebar_menu_item_color: defaultTheme.sidebar_menu_item_color || '#b3b3b3',
                                    sidebar_menu_item_hover_bg_color: defaultTheme.sidebar_menu_item_hover_bg_color || '#5E5BFF',
                                    sidebar_menu_item_active_color: defaultTheme.sidebar_menu_item_active_color || '#5E5BFF',
                                    sidebar_menu_opacity: defaultTheme.sidebar_menu_opacity || '0.5',
                                    login_buttons_color: defaultTheme.login_buttons_color || '#5E5BFF',
                                    login_buttons_text_color: defaultTheme.login_buttons_text_color || '#FFFFFF',
                                    login_buttons_hover_color: defaultTheme.login_buttons_hover_color || '#4A47CC',
                                    login_buttons_hover_text_color: defaultTheme.login_buttons_hover_text_color || '#FFFFFF',
                                    signup_buttons_color: defaultTheme.signup_buttons_color || '#5E5BFF',
                                    signup_buttons_text_color: defaultTheme.signup_buttons_text_color || '#FFFFFF',
                                    signup_buttons_hover_color: defaultTheme.signup_buttons_hover_color || '#4A47CC',
                                    signup_buttons_hover_text_color: defaultTheme.signup_buttons_hover_text_color || '#FFFFFF',
                                    guest_buttons_color: defaultTheme.guest_buttons_color || '#FFFFFF',
                                    guest_buttons_text_color: defaultTheme.guest_buttons_text_color || '#5E5BFF',
                                    guest_buttons_hover_color: defaultTheme.guest_buttons_hover_color || '#FFFFFF',
                                    guest_buttons_hover_text_color: defaultTheme.guest_buttons_hover_text_color || '#4A47CC',
                                });
                            }
                        }
                        
                        // Clear editing state if we were editing the deleted theme
                        if (editingThemeId === theme.id) {
                            setEditingThemeId(null);
                            setEditingThemeName('');
                        }
                        
                        // Refresh themes list
                        setThemesLoaded(false);
                        fetchThemes();
                    } else {
                        message.error(data.error || t('admin.customization.themeDeleteFailed'));
                    }
                } catch (error) {
                    console.error('Error deleting theme:', error);
                    message.error(t('admin.customization.themeDeleteFailed'));
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const saveTheme = async () => {
        if (!selectedTheme) return;
        
        try {
            setLoading(true);
            
            // Determine if this is the current theme (matches currentThemeId)
            const isCurrentTheme = selectedTheme.id === currentThemeId;
            
            // Prepare the update payload
            const updatePayload = {
                id: selectedTheme.id,
                is_current: isCurrentTheme,
                ...currentColors
            };
            
            // Include theme name if theme is being edited
            if (editingThemeId === selectedTheme.id && editingThemeName.trim()) {
                updatePayload.theme_name = editingThemeName.trim();
            }
            
            const response = await fetch('/api/customization/themes/update/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(updatePayload),
            });

            const data = await response.json();
            
            if (data.success || data.message) {
                if (isCurrentTheme) {
                    localStorage.setItem('themeUpdateSuccess', data.message || t('admin.customization.themeCurrentUpdatedSuccess'));
                    const hash = window.location.hash ? window.location.hash.slice(1) : '';
                    if (hash) localStorage.setItem('settingsHashAfterReload', hash);
                    window.location.reload();
                } else {
                    // Clear editing state first before other updates
                    if (editingThemeId === selectedTheme.id) {
                        setEditingThemeId(null);
                        setEditingThemeName('');
                    }
                    
                    message.success(data.message || t('admin.customization.themePresetSaveSuccess'));
                    setThemesLoaded(false); // Reset flag to allow refetch
                    fetchThemes(); // Refresh themes
                }
            } else {
                message.error(data.error || t('admin.customization.themePresetSaveFailed'));
            }
        } catch (error) {
            console.error('Error updating theme:', error);
            message.error(t('admin.customization.themePresetSaveFailed'));
        } finally {
            setLoading(false);
        }
    };

    const createNewPreset = async () => {
        try {
            setLoading(true);
            
            const response = await fetch('/api/customization/themes/create/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    name: t('admin.customization.themePresetDefaultName', { n: themes.length + 1 }),
                    ...currentColors
                }),
            });

            const data = await response.json();
            
            if (data.success) {
                message.success(t('admin.customization.themePresetCreateSuccess'));
                setThemesLoaded(false); // Reset flag to allow refetch
                fetchThemes(); // Refresh themes
                setSelectedTheme(data.customization_theme);
            } else {
                message.error(data.error || t('admin.customization.themePresetCreateFailed'));
            }
        } catch (error) {
            console.error('Error creating new preset:', error);
            message.error(t('admin.customization.themePresetCreateFailed'));
        } finally {
            setLoading(false);
        }
    };

    const resetColorsToDefaults = () => {
        Modal.confirm({
            title: t('admin.customization.themeColorsResetModalTitle'),
            icon: null,
            content: null,
            okText: t('common.buttons.confirm'),
            cancelText: t('common.buttons.cancel'),
            okType: 'primary',
            okButtonProps: {
                style: { backgroundColor: '#52c41a', borderColor: '#52c41a' }
            },
            onOk: async () => {
                try {
                    setLoading(true);
                    
                    const response = await fetch('/api/customization/themes/set-current/', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            theme_id: selectedTheme.id,
                            reset: true
                        }),
                    });

                    const data = await response.json();
                    
                    if (data.success) {
                        localStorage.setItem('themeUpdateSuccess', data.message || 'Theme colors reset to defaults successfully');
                        const hash = window.location.hash ? window.location.hash.slice(1) : '';
                        if (hash) localStorage.setItem('settingsHashAfterReload', hash);
                        window.location.reload();
                    } else {
                        message.error(data.error || t('admin.customization.themeColorsResetFailed'));
                    }
                } catch (error) {
                    console.error('Error resetting theme colors to defaults:', error);
                    message.error(t('admin.customization.themeColorsResetFailed'));
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    if (initialLoading) {
        return (
            <div className="customization-container"></div>
        );
    }

    return (
        <>
        <div className={`customization-container ${embedded ? 'customization-embedded' : ''} ${unifiedPage ? 'customization-unified' : ''}`}>
            <div className="customization-card">
                <div className={`customization-split ${embedded ? 'customization-embedded' : ''}`}>
                    <div className="customization-main">
                        <div className={`customization-scroll-area ${unifiedPage ? 'customization-scroll-area-unified' : ''}`} ref={contentRef} data-settings-scroll>
                            <Spin spinning={loading}>
                                <div className="customization-content">
                                    <div id="customization-section-branding" className="customization-section customization-scroll-section">
                            <h2 className="customization-section-title">{t('admin.customization.branding')}</h2>
                            <form onSubmit={handleSubmit} className="customization-form">
                                <div className="customization-card-layout">
                                    <div className="customization-card-item">
                                        <div className="customization-card-header customization-card-header-with-input">
                                            <div className="customization-card-title-section">
                                                <h3 className="customization-card-title">{t('admin.customization.companyName')}</h3>
                                                <p className="customization-card-description">{t('admin.customization.companyNameCardDescription')}</p>
                                            </div>
                                            <div className="customization-card-input-section">
                                                <input
                                                    type="text"
                                                    value={formData.company_name || ''}
                                                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                                                    placeholder={t('admin.customization.companyNamePlaceholder')}
                                                    required
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="customization-card-item">
                                        <div className="customization-card-header">
                                            <div className="customization-card-title-section">
                                                <h3 className="customization-card-title">{t('admin.customization.logo')}</h3>
                                                <p className="customization-card-description">{t('admin.customization.logoDescription')}</p>
                                            </div>
                                        </div>
                                        <div className="customization-card-content">
                                            <div className="customization-form-group">
                                                <div className="customization-logo-actions">
                                                    <label className="customization-upload-button">
                                                        <span>{t('admin.customization.uploadLogo')}</span>
                                                        <input
                                                            type="file"
                                                            accept=".png,.jpg,.jpeg,.gif,.webp,.svg"
                                                            onChange={handleLogoUpload}
                                                            disabled={loading}
                                                            style={{ display: 'none' }}
                                                        />
                                                    </label>
                                                    <span className="customization-logo-or">{t('admin.customization.or')}</span>
                                                    <input
                                                        type="url"
                                                        className="customization-logo-url-input"
                                                        value={formData.logo_url || ''}
                                                        onChange={(e) => handleInputChange('logo_url', e.target.value)}
                                                        placeholder={t('admin.customization.logoUrlPlaceholder')}
                                                        disabled={loading}
                                                    />
                                                </div>
                                                {formData.logo_url && (
                                                    <div className="customization-logo-preview">
                                                        <img src={formData.logo_url} alt={t('admin.customization.logoPreviewAlt')} style={{maxWidth: '200px', maxHeight: '100px'}} onError={(e) => { e.target.style.display = 'none'; }} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                                    </div>
                                    <div id="customization-section-contact" className="customization-section customization-scroll-section">
                            <h2 className="customization-section-title">{t('admin.customization.contact')}</h2>
                            <div className="customization-card-layout">
                                {/* Email Contact */}
                                <div className="customization-card-item customization-card-clickable">
                                    <div 
                                        className="customization-card-header"
                                        onClick={() => handleProviderToggle('email')}
                                    >
                                        <div className="customization-card-icon icon-rose">
                                            <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: '20px' }} />
                                        </div>
                                        <div className="customization-card-title-section">
                                            <h3 className="customization-card-title">{t('admin.customization.contactEmailSectionTitle')}</h3>
                                            <p className="customization-card-description">{t('admin.customization.contactEmailSectionDescription')}</p>
                                            <span className="customization-field-hint" style={{ marginTop: 4, display: 'block' }}>
                                                {formData.contact_email ? t('admin.customization.fieldStatusConfigured') : t('admin.customization.fieldStatusNotConfigured')}
                                            </span>
                                        </div>
                                        <div className="contact-provider-toggle">
                                            <div className={`contact-chevron ${expandedProviders.email ? 'expanded' : ''}`}>
                                                {expandedProviders.email ? <DownOutlined /> : <RightOutlined />}
                                            </div>
                                        </div>
                                    </div>
                                    {expandedProviders.email && (
                                        <div className="customization-card-content">
                                            <div className="customization-contact-fields">
                                                <div className="customization-contact-field-item">
                                                    <div className="customization-contact-field-info">
                                                        <FontAwesomeIcon icon={faEnvelope} className="customization-contact-field-icon" />
                                                        <span className="customization-contact-field-label">{t('admin.customization.contactEmail')}</span>
                                                    </div>
                                                    <div className="customization-contact-field-input">
                                                        <input
                                                            type="email"
                                                            value={formData.contact_email || ''}
                                                            onChange={(e) => handleInputChange('contact_email', e.target.value)}
                                                            placeholder={t('admin.customization.contactEmailPlaceholder')}
                                                            required
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="customization-contact-field-item">
                                                    <div className="customization-contact-field-info">
                                                        <FontAwesomeIcon icon={faGlobe} className="customization-contact-field-icon" />
                                                        <span className="customization-contact-field-label">{t('admin.customization.companyWebsite')}</span>
                                                    </div>
                                                    <div className="customization-contact-field-input">
                                                        <input
                                                            type="url"
                                                            value={formData.company_website || ''}
                                                            onChange={(e) => handleInputChange('company_website', e.target.value)}
                                                            placeholder={t('admin.customization.companyWebsitePlaceholder')}
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Social Media */}
                                <div className="customization-card-item customization-card-clickable">
                                    <div 
                                        className="customization-card-header"
                                        onClick={() => handleProviderToggle('social')}
                                    >
                                        <div className="customization-card-icon icon-blue">
                                            <FontAwesomeIcon icon={faShareNodes} style={{ fontSize: '20px' }} />
                                        </div>
                                        <div className="customization-card-title-section">
                                            <h3 className="customization-card-title">{t('admin.customization.socialMediaSectionTitle')}</h3>
                                            <p className="customization-card-description">{t('admin.customization.socialMediaSectionDescription')}</p>
                                            <span className="customization-field-hint" style={{ marginTop: 4, display: 'block' }}>
                                                {t('admin.customization.platformsConfiguredCount', {
                                                    count: Object.entries(formData).filter(([key, value]) =>
                                                        ['contact_github', 'contact_facebook', 'contact_instagram', 'contact_linkedin', 'contact_twitter', 'contact_youtube', 'contact_blog'].includes(key) && value
                                                    ).length,
                                                    total: 7
                                                })}
                                            </span>
                                        </div>
                                        <div className="contact-provider-toggle">
                                            <div className={`contact-chevron ${expandedProviders.social ? 'expanded' : ''}`}>
                                                {expandedProviders.social ? <DownOutlined /> : <RightOutlined />}
                                            </div>
                                        </div>
                                    </div>
                                    {expandedProviders.social && (
                                        <div className="customization-card-content">
                                            <div className="customization-contact-fields">
                                                <div className="customization-contact-field-item">
                                                    <div className="customization-contact-field-info">
                                                        <FontAwesomeIcon icon={faCodeBranch} className="customization-contact-field-icon" />
                                                        <span className="customization-contact-field-label">{t('admin.customization.githubUrl')}</span>
                                                    </div>
                                                    <div className="customization-contact-field-input">
                                                        <input
                                                            type="url"
                                                            value={formData.contact_github || ''}
                                                            onChange={(e) => handleInputChange('contact_github', e.target.value)}
                                                            placeholder={t('admin.customization.githubUrlPlaceholder')}
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                                
                                                <div className="customization-contact-field-item">
                                                    <div className="customization-contact-field-info">
                                                        <FontAwesomeIcon icon={faUser} className="customization-contact-field-icon" />
                                                        <span className="customization-contact-field-label">{t('admin.customization.facebookUrl')}</span>
                                                    </div>
                                                    <div className="customization-contact-field-input">
                                                        <input
                                                            type="url"
                                                            value={formData.contact_facebook || ''}
                                                            onChange={(e) => handleInputChange('contact_facebook', e.target.value)}
                                                            placeholder={t('admin.customization.facebookUrlPlaceholder')}
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                                
                                                <div className="customization-contact-field-item">
                                                    <div className="customization-contact-field-info">
                                                        <FontAwesomeIcon icon={faCamera} className="customization-contact-field-icon" />
                                                        <span className="customization-contact-field-label">{t('admin.customization.instagramUrl')}</span>
                                                    </div>
                                                    <div className="customization-contact-field-input">
                                                        <input
                                                            type="url"
                                                            value={formData.contact_instagram || ''}
                                                            onChange={(e) => handleInputChange('contact_instagram', e.target.value)}
                                                            placeholder={t('admin.customization.instagramUrlPlaceholder')}
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                                
                                                <div className="customization-contact-field-item">
                                                    <div className="customization-contact-field-info">
                                                        <FontAwesomeIcon icon={faBriefcase} className="customization-contact-field-icon" />
                                                        <span className="customization-contact-field-label">{t('admin.customization.linkedinUrl')}</span>
                                                    </div>
                                                    <div className="customization-contact-field-input">
                                                        <input
                                                            type="url"
                                                            value={formData.contact_linkedin || ''}
                                                            onChange={(e) => handleInputChange('contact_linkedin', e.target.value)}
                                                            placeholder={t('admin.customization.linkedinUrlPlaceholder')}
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                                
                                                <div className="customization-contact-field-item">
                                                    <div className="customization-contact-field-info">
                                                        <FontAwesomeIcon icon={faHashtag} className="customization-contact-field-icon" />
                                                        <span className="customization-contact-field-label">{t('admin.customization.twitterUrl')}</span>
                                                    </div>
                                                    <div className="customization-contact-field-input">
                                                        <input
                                                            type="url"
                                                            value={formData.contact_twitter || ''}
                                                            onChange={(e) => handleInputChange('contact_twitter', e.target.value)}
                                                            placeholder={t('admin.customization.twitterUrlPlaceholder')}
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                                
                                                <div className="customization-contact-field-item">
                                                    <div className="customization-contact-field-info">
                                                        <FontAwesomeIcon icon={faPlay} className="customization-contact-field-icon" />
                                                        <span className="customization-contact-field-label">{t('admin.customization.youtubeUrl')}</span>
                                                    </div>
                                                    <div className="customization-contact-field-input">
                                                        <input
                                                            type="url"
                                                            value={formData.contact_youtube || ''}
                                                            onChange={(e) => handleInputChange('contact_youtube', e.target.value)}
                                                            placeholder={t('admin.customization.youtubeUrlPlaceholder')}
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                                
                                                <div className="customization-contact-field-item">
                                                    <div className="customization-contact-field-info">
                                                        <FontAwesomeIcon icon={faNewspaper} className="customization-contact-field-icon" />
                                                        <span className="customization-contact-field-label">{t('admin.customization.blogUrl')}</span>
                                                    </div>
                                                    <div className="customization-contact-field-input">
                                                        <input
                                                            type="url"
                                                            value={formData.contact_blog || ''}
                                                            onChange={(e) => handleInputChange('contact_blog', e.target.value)}
                                                            placeholder={t('admin.customization.blogUrlPlaceholder')}
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                </div>
                                    </div>
                                    <div id="customization-section-theme" className="customization-section customization-scroll-section">
                            <div className="theme-main-header">
                                <h2 className="customization-section-title">{t('admin.customization.theme')}</h2>
                                <div className="theme-main-tabs">
                                    {THEME_TABS.map((tab) => (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            className={`theme-main-tab ${themeTab === tab.id ? 'active' : ''}`}
                                            onClick={() => {
                                                setThemeTab(tab.id);
                                                localStorage.setItem('customizationThemeTab', tab.id);
                                            }}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className={`theme-top-section ${themeTab === 'presets' ? 'theme-top-section--presets' : ''} ${themeTab === 'layout' || themeTab === 'buttons' ? 'theme-top-section--options' : ''}`}>
                                <div className="theme-top-section-content">
                                {themeTab === 'presets' && (() => {
                                    const allThemes = defaultTheme ? [defaultTheme, ...themes] : themes;
                                    const gridThemes = allThemes.filter(th => th.id !== currentThemeId);
                                    const current = defaultTheme && currentThemeId === defaultTheme.id ? defaultTheme : themes.find(th => th.id === currentThemeId) || defaultTheme;
                                    const totalPages = Math.max(1, Math.ceil(gridThemes.length / presetsPerPage));
                                    const start = (presetsPage - 1) * presetsPerPage;
                                    const pageThemes = gridThemes.slice(start, start + presetsPerPage);
                                    return (
                                        <div className="theme-presets-layout">
                                            {current ? (
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={`${t('admin.customization.currentThemeLabel')}: ${current.name}`}
                                                    className={`theme-active-strip ${selectedTheme?.id === current.id ? 'selected' : ''}`}
                                                    onClick={() => handleThemeSelect(current)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            handleThemeSelect(current);
                                                        }
                                                    }}
                                                >
                                                    <div className="theme-active-strip-main">
                                                        <span className="theme-active-strip-badge">
                                                            <FontAwesomeIcon icon={faCheck} className="theme-active-strip-badge-icon" aria-hidden />
                                                            {t('admin.customization.themeAppliedBadge')}
                                                        </span>
                                                        <div className="theme-active-strip-info">
                                                            <span className="theme-active-strip-label">{t('admin.customization.currentThemeLabel')}</span>
                                                            <span className="theme-active-strip-name">{current.name}</span>
                                                        </div>
                                                    </div>
                                                    <div className="theme-active-strip-swatches" aria-hidden>
                                                        <span className="theme-active-strip-swatch" style={{ backgroundColor: current.layout_border_color }} />
                                                        <span className="theme-active-strip-swatch" style={{ backgroundColor: current.confirm_buttons_color }} />
                                                        <span className="theme-active-strip-swatch" style={{ backgroundColor: current.cancel_buttons_color }} />
                                                        <span className="theme-active-strip-swatch" style={{ backgroundColor: current.pills_color }} />
                                                    </div>
                                                </div>
                                            ) : null}
                                            {gridThemes.length > 0 && (
                                                <div className="theme-presets-gallery">
                                                    <h3 className="theme-presets-gallery-title">{t('admin.customization.themeGalleryHeading')}</h3>
                                                    {totalPages > 1 && (
                                                        <div className="theme-presets-pagination">
                                                            <button type="button" className="theme-pagination-btn" onClick={() => setPresetsPage(p => Math.max(1, p - 1))} disabled={presetsPage <= 1}>←</button>
                                                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                                                                <button key={p} type="button" className={`theme-pagination-dot ${presetsPage === p ? 'active' : ''}`} onClick={() => setPresetsPage(p)} aria-label={t('admin.customization.themePaginationPage', { page: p })} />
                                                            ))}
                                                            <button type="button" className="theme-pagination-btn" onClick={() => setPresetsPage(p => Math.min(totalPages, p + 1))} disabled={presetsPage >= totalPages}>→</button>
                                                        </div>
                                                    )}
                                                    <div className="theme-presets-grid-measure" ref={presetsRowMeasureRef}>
                                                        <div className="theme-presets-grid" role="list">
                                                            {pageThemes.map((theme) => (
                                                                <div
                                                                    key={theme.id}
                                                                    role="listitem"
                                                                    className={`theme-preset-card ${selectedTheme?.id === theme.id ? 'selected' : ''}`}
                                                                    onClick={() => handleThemeSelect(theme)}
                                                                >
                                                                    <div className="theme-preset-card-title">{theme.name}</div>
                                                                    <div className="theme-preset-card-colors">
                                                                        <span className="theme-preset-card-swatch" style={{ backgroundColor: theme.layout_border_color }} />
                                                                        <span className="theme-preset-card-swatch" style={{ backgroundColor: theme.confirm_buttons_color }} />
                                                                        <span className="theme-preset-card-swatch" style={{ backgroundColor: theme.cancel_buttons_color }} />
                                                                        <span className="theme-preset-card-swatch" style={{ backgroundColor: theme.pills_color }} />
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                                {themeTab === 'layout' && (
                                    <div className="theme-options-area theme-options-area--split">
                                        <div className="theme-options-left">
                                            <div className="theme-options-scroll">
                                                <div className="theme-options-group">
                                                    <div className="theme-options-column-label">{t('admin.customization.themeLayoutAndBorder')}</div>
                                                    {layoutOptions.map((opt) => (
                                                        <label key={opt.key} className="theme-option-row">
                                                            <input type="radio" name="layoutColor" value={opt.key} checked={selectedColorOption === opt.key} onChange={() => handleColorOptionSelect(opt.key)} />
                                                            <span className="theme-option-swatch" style={{ backgroundColor: currentColors[opt.key] }} />
                                                            <span className="theme-option-label">{opt.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                                <div className="theme-options-group">
                                                    <div className="theme-options-column-label">{t('admin.customization.themePillsGroup')}</div>
                                                    {pillsOptions.map((opt) => (
                                                        <label key={opt.key} className="theme-option-row">
                                                            <input type="radio" name="layoutColor" value={opt.key} checked={selectedColorOption === opt.key} onChange={() => handleColorOptionSelect(opt.key)} />
                                                            <span className="theme-option-swatch" style={{ backgroundColor: currentColors[opt.key] }} />
                                                            <span className="theme-option-label">{opt.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                                <div className="theme-options-group">
                                                    <div className="theme-options-column-label">{t('admin.customization.sidebarMenu')}</div>
                                                    {sidebarOptions.map((opt) => (
                                                        <label key={opt.key} className="theme-option-row">
                                                            <input type="radio" name="layoutColor" value={opt.key} checked={selectedColorOption === opt.key} onChange={() => handleColorOptionSelect(opt.key)} />
                                                            <span className="theme-option-swatch" style={opt.type === 'opacity' ? { backgroundColor: '#888', opacity: parseFloat(currentColors[opt.key]) || 0.5 } : { backgroundColor: currentColors[opt.key] }} />
                                                            <span className="theme-option-label">{opt.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="theme-options-right">
                                            {selectedColorOption === 'sidebar_menu_opacity' && (
                                                <div className="theme-color-editor">
                                                    <h4 className="theme-color-editor-title">{t('admin.customization.opacity')}</h4>
                                                    <div className="theme-opacity-control">
                                                        <input type="range" min="0" max="1" step="0.1" value={parseFloat(currentColors.sidebar_menu_opacity) || 0.5} onChange={(e) => handleOpacityChange(e.target.value)} disabled={loading} />
                                                        <input type="number" min="0" max="1" step="0.1" value={parseFloat(currentColors.sidebar_menu_opacity) || 0.5} onChange={(e) => handleOpacityChange(e.target.value)} disabled={loading} />
                                                    </div>
                                                </div>
                                            )}
                                            {selectedColorOption && selectedColorOption !== 'sidebar_menu_opacity' && (layoutOptions.some(o => o.key === selectedColorOption) || pillsOptions.some(o => o.key === selectedColorOption) || sidebarOptions.some(o => o.key === selectedColorOption && o.type !== 'opacity')) && (
                                                <div className="theme-color-editor">
                                                    <h4 className="theme-color-editor-title">{(layoutOptions.find(o => o.key === selectedColorOption) || pillsOptions.find(o => o.key === selectedColorOption) || sidebarOptions.find(o => o.key === selectedColorOption))?.label}</h4>
                                                    <RgbColorSelector value={currentColors[selectedColorOption]} onChange={(v) => handleColorChange(selectedColorOption, v)} disabled={loading} />
                                                    <div className="theme-color-values">
                                                        <div className="theme-color-hex">
                                                            <span>{t('admin.customization.colorPickerHex')}</span>
                                                            <input type="text" value={currentColors[selectedColorOption]} onChange={(e) => handleHexInputChange(selectedColorOption, e.target.value)} placeholder="#000000" maxLength={7} disabled={loading} />
                                                        </div>
                                                        <div className="theme-color-rgb">
                                                            <span>{t('admin.customization.colorPickerRgb')}</span>
                                                            {(() => {
                                                                const rgb = hexToRgb(currentColors[selectedColorOption]);
                                                                return ['r', 'g', 'b'].map((c) => (
                                                                    <div key={c} className="theme-color-rgb-input-wrap">
                                                                        <button type="button" className="theme-color-rgb-chevron" onClick={() => handleRgbStep(selectedColorOption, c, 1)} disabled={loading} aria-label={t('admin.customization.colorPickerIncreaseChannel', { channel: rgbChannelLabels[c] })}>
                                                                            <FontAwesomeIcon icon={faChevronUp} />
                                                                        </button>
                                                                        <input type="number" value={rgb?.[c] ?? 0} onChange={(e) => handleRgbInputChange(selectedColorOption, c, e.target.value)} min={0} max={255} disabled={loading} />
                                                                        <button type="button" className="theme-color-rgb-chevron" onClick={() => handleRgbStep(selectedColorOption, c, -1)} disabled={loading} aria-label={t('admin.customization.colorPickerDecreaseChannel', { channel: rgbChannelLabels[c] })}>
                                                                            <FontAwesomeIcon icon={faChevronDown} />
                                                                        </button>
                                                                    </div>
                                                                ));
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {themeTab === 'buttons' && (
                                    <div className="theme-options-area theme-options-area--split">
                                        <div className="theme-options-left">
                                            <div className="theme-options-scroll">
                                                <div className="theme-options-group">
                                                    <div className="theme-options-column-label">{t('admin.customization.themeConfirmGroup')}</div>
                                                    {confirmButtonOptions.map((opt) => (
                                                        <label key={opt.key} className="theme-option-row">
                                                            <input type="radio" name="buttonColor" value={opt.key} checked={selectedColorOption === opt.key} onChange={() => handleColorOptionSelect(opt.key)} />
                                                            <span className="theme-option-swatch" style={{ backgroundColor: currentColors[opt.key] }} />
                                                            <span className="theme-option-label">{opt.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                                <div className="theme-options-group">
                                                    <div className="theme-options-column-label">{t('admin.customization.themeCancelGroup')}</div>
                                                    {cancelButtonOptions.map((opt) => (
                                                        <label key={opt.key} className="theme-option-row">
                                                            <input type="radio" name="buttonColor" value={opt.key} checked={selectedColorOption === opt.key} onChange={() => handleColorOptionSelect(opt.key)} />
                                                            <span className="theme-option-swatch" style={{ backgroundColor: currentColors[opt.key] }} />
                                                            <span className="theme-option-label">{opt.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                                <div className="theme-options-group">
                                                    <div className="theme-options-column-label">{t('admin.customization.loginButton')}</div>
                                                    {loginButtonOptions.map((opt) => (
                                                        <label key={opt.key} className="theme-option-row">
                                                            <input type="radio" name="buttonColor" value={opt.key} checked={selectedColorOption === opt.key} onChange={() => handleColorOptionSelect(opt.key)} />
                                                            <span className="theme-option-swatch" style={{ backgroundColor: currentColors[opt.key] }} />
                                                            <span className="theme-option-label">{opt.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                                <div className="theme-options-group">
                                                    <div className="theme-options-column-label">{t('admin.customization.signupButton')}</div>
                                                    {signupButtonOptions.map((opt) => (
                                                        <label key={opt.key} className="theme-option-row">
                                                            <input type="radio" name="buttonColor" value={opt.key} checked={selectedColorOption === opt.key} onChange={() => handleColorOptionSelect(opt.key)} />
                                                            <span className="theme-option-swatch" style={{ backgroundColor: currentColors[opt.key] }} />
                                                            <span className="theme-option-label">{opt.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                                <div className="theme-options-group">
                                                    <div className="theme-options-column-label">{t('admin.customization.guestButton')}</div>
                                                    {guestButtonOptions.map((opt) => (
                                                        <label key={opt.key} className="theme-option-row">
                                                            <input type="radio" name="buttonColor" value={opt.key} checked={selectedColorOption === opt.key} onChange={() => handleColorOptionSelect(opt.key)} />
                                                            <span className="theme-option-swatch" style={{ backgroundColor: currentColors[opt.key] }} />
                                                            <span className="theme-option-label">{opt.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="theme-options-right">
                                            {selectedColorOption && (confirmButtonOptions.some(o => o.key === selectedColorOption) || cancelButtonOptions.some(o => o.key === selectedColorOption) || loginButtonOptions.some(o => o.key === selectedColorOption) || signupButtonOptions.some(o => o.key === selectedColorOption) || guestButtonOptions.some(o => o.key === selectedColorOption)) && (
                                                <div className="theme-color-editor">
                                                    <h4 className="theme-color-editor-title">{(confirmButtonOptions.find(o => o.key === selectedColorOption) || cancelButtonOptions.find(o => o.key === selectedColorOption) || loginButtonOptions.find(o => o.key === selectedColorOption) || signupButtonOptions.find(o => o.key === selectedColorOption) || guestButtonOptions.find(o => o.key === selectedColorOption))?.label}</h4>
                                                    <RgbColorSelector value={currentColors[selectedColorOption]} onChange={(v) => handleColorChange(selectedColorOption, v)} disabled={loading} />
                                                    <div className="theme-color-values">
                                                        <div className="theme-color-hex">
                                                            <span>{t('admin.customization.colorPickerHex')}</span>
                                                            <input type="text" value={currentColors[selectedColorOption]} onChange={(e) => handleHexInputChange(selectedColorOption, e.target.value)} placeholder="#000000" maxLength={7} disabled={loading} />
                                                        </div>
                                                        <div className="theme-color-rgb">
                                                            <span>{t('admin.customization.colorPickerRgb')}</span>
                                                                {(() => {
                                                                const rgb = hexToRgb(currentColors[selectedColorOption]);
                                                                return ['r', 'g', 'b'].map((c) => (
                                                                    <div key={c} className="theme-color-rgb-input-wrap">
                                                                        <button type="button" className="theme-color-rgb-chevron" onClick={() => handleRgbStep(selectedColorOption, c, 1)} disabled={loading} aria-label={t('admin.customization.colorPickerIncreaseChannel', { channel: rgbChannelLabels[c] })}>
                                                                            <FontAwesomeIcon icon={faChevronUp} />
                                                                        </button>
                                                                        <input type="number" value={rgb?.[c] ?? 0} onChange={(e) => handleRgbInputChange(selectedColorOption, c, e.target.value)} min={0} max={255} disabled={loading} />
                                                                        <button type="button" className="theme-color-rgb-chevron" onClick={() => handleRgbStep(selectedColorOption, c, -1)} disabled={loading} aria-label={t('admin.customization.colorPickerDecreaseChannel', { channel: rgbChannelLabels[c] })}>
                                                                            <FontAwesomeIcon icon={faChevronDown} />
                                                                        </button>
                                                                    </div>
                                                                ));
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                </div>
                                <div className="theme-top-section-actions">
                                    {themeTab === 'presets' && (
                                        <>
                                            {selectedTheme?.id === currentThemeId && (
                                                <button type="button" className="customization-reset-button" onClick={resetColorsToDefaults} disabled={loading} title={t('admin.customization.themeResetButtonTooltip')}>
                                                    <FontAwesomeIcon icon={faUndo} /> {t('admin.customization.themeResetButton')}
                                                </button>
                                            )}
                                            <button type="button" className="customization-new-preset-button" onClick={createNewPreset} disabled={loading} title={t('admin.customization.themeNewPresetTooltip')}>
                                                <FontAwesomeIcon icon={faSave} /> {t('admin.customization.themeNewPresetButton')}
                                            </button>
                                            {selectedTheme && selectedTheme.id !== currentThemeId && (
                                                <button type="button" className="customization-set-current-button" onClick={() => handleRadioChange(selectedTheme)} disabled={loading} title={t('admin.customization.themeSetCurrentTooltip')}>
                                                    <FontAwesomeIcon icon={faSave} /> {t('admin.customization.themeSetCurrentButton')}
                                                </button>
                                            )}
                                        </>
                                    )}
                                    <button type="button" className="customization-save-button" onClick={saveTheme} disabled={loading || !selectedTheme} title={t('admin.customization.themeApplyTooltip')}>
                                        <FontAwesomeIcon icon={faSave} /> {t('admin.customization.themeApplyButton')}
                                    </button>
                                </div>
                            </div>

                            <div className="theme-bottom-section">
                                <ThemeLivePreview sidebarTheme={activeSection === 'theme' ? currentColors : savedCustomization} />
                            </div>
                        </div>
                                </div>
                            </Spin>
                        </div>
                    </div>
                </div>
            </div>
        </div>

            {/* Set Current Theme Confirmation Modal */}
            {showSetCurrentConfirm && (
                <div className="theme-confirm-overlay">
                    <div className="theme-confirm-modal">
                        <div className="theme-confirm-header">
                            <h3>{t('admin.customization.themeSetCurrentModalTitle')}</h3>
                        </div>
                        <div className="theme-confirm-content">
                            <p>{t('admin.customization.themeSetCurrentModalBody', { name: themeToSetCurrent?.name ?? '' })}</p>
                            <p>{t('admin.customization.themeSetCurrentModalSubtext')}</p>
                        </div>
                        <div className="theme-confirm-actions">
                            <button 
                                className="theme-confirm-cancel" 
                                onClick={cancelSetCurrentTheme}
                                disabled={loading}
                            >
                                {t('common.buttons.cancel')}
                            </button>
                            <button 
                                className="theme-confirm-apply" 
                                onClick={confirmSetCurrentTheme}
                                disabled={loading}
                            >
                                {loading ? t('admin.customization.themeApplying') : t('admin.customization.themeApplyTheme')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Customization; 